import { t } from "@lingui/macro";
import { Button, Input, Label } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { CheckCircle2, Plus, Sparkles } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { aiService } from "@/client/services/aiService";
import { useAudioChatStore, type workExpInfoType } from "@/client/stores/audioChatStore";

import {
  recordLegacyEvent,
  recordLegacyMessage,
  recordLegacyMessages,
  recordLegacyStateUpdate,
  recordLegacyToolFinished,
  recordLegacyToolStarted,
} from "./_utils/legacyTranscriptBridge";
import { TypingText } from "./typingText";

type Message = {
  type: "user" | "ai";
  content: string;
  showIllustration?: string;
};
type messageType = Message[];
type WorkExperienceField = "company" | "duration" | "title" | "impact";
type WorkExpChatProps = {
  focusTextarea: () => void;
};

const workExperiencePromptId = "work-experience-cards";
const generateExperienceSuggestionsToolName = "generate_experience_suggestions";
const generateUserSummaryToolName = "generate_user_summary";

const emptyWorkExperience: workExpInfoType = {
  jobCompanyName: "",
  jobtitleAndResp: "",
  jobDuration: "",
  aiSugg: [],
  jobUniqueImpact: "",
};

const hasWorkExperienceContent = (experience: workExpInfoType) =>
  Boolean(
    experience.jobCompanyName.trim() ||
      experience.jobtitleAndResp.trim() ||
      experience.jobDuration.trim() ||
      experience.jobUniqueImpact.trim(),
  );

const hasUsableWorkExperience = (experience: workExpInfoType) =>
  Boolean(experience.jobCompanyName.trim() && experience.jobtitleAndResp.trim());

const countFilledWorkExperiences = (workExperiences: workExpInfoType[]) =>
  workExperiences.filter(hasWorkExperienceContent).length;

export const WorkExpChat = ({ focusTextarea }: WorkExpChatProps) => {
  const [showWorkExp, setShowWorkExp] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [messages, setMessages] = useState<messageType>([]);
  const [workExpError, setWorkExpError] = useState("");
  const [suggestionLoadingIndex, setSuggestionLoadingIndex] = useState<number | null>(null);
  const step = useAudioChatStore((state) => state.step);
  const chatTextData = useAudioChatStore((state) => state.chatTextData);
  const jobDescription = useAudioChatStore((state) => state.jobDescription);
  const workExpInfo = useAudioChatStore((state) => state.workExpInfo);
  const setIsLoading = useAudioChatStore((state) => state.setIsLoading);
  const setStep = useAudioChatStore((state) => state.setStep);
  const updateWorkExpInfoCompany = useAudioChatStore((state) => state.updateWorkExpInfoCompany);
  const updateWorkExpInfoTitle = useAudioChatStore((state) => state.updateWorkExpInfoTitle);
  const updateWorkExpInfoDuration = useAudioChatStore((state) => state.updateWorkExpInfoDuration);
  const updateWorkExpInfoAiSugg = useAudioChatStore((state) => state.updateWorkExpInfoAiSugg);
  const updateWorkExpInfojobExp = useAudioChatStore((state) => state.updateWorkExpInfojobExp);
  const updateWorkExpInfoAddNewJob = useAudioChatStore((state) => state.updateWorkExpInfoAddNewJob);
  const setChatTextData = useAudioChatStore((state) => state.setChatTextData);
  const updateUserSummary = useAudioChatStore((state) => state.updateUserSummary);

  const visibleWorkExperiences = useMemo(
    () => (workExpInfo.length > 0 ? workExpInfo : [emptyWorkExperience]),
    [workExpInfo],
  );
  const filledWorkExperienceCount = countFilledWorkExperiences(workExpInfo);

  const pushMessages = useCallback((mes: Message) => {
    setMessages((prevMessages) => [...prevMessages, mes]);
    recordLegacyMessage(mes);
  }, []);

  const updateWorkExperienceField = (index: number, field: WorkExperienceField, value: string) => {
    switch (field) {
      case "company":
        updateWorkExpInfoCompany(index, value);
        break;
      case "duration":
        updateWorkExpInfoDuration(index, value);
        break;
      case "title":
        updateWorkExpInfoTitle(index, value);
        break;
      case "impact":
        updateWorkExpInfojobExp(index, value);
        break;
    }

    setWorkExpError("");
    recordLegacyStateUpdate(`experience.${index}.${field}`, value);
  };

  const handleAddWorkExperience = () => {
    const lastExperience = visibleWorkExperiences[visibleWorkExperiences.length - 1];
    if (lastExperience && !hasWorkExperienceContent(lastExperience)) {
      setWorkExpError(t`Add details to the current experience first.`);
      return;
    }

    updateWorkExpInfoAddNewJob();
    recordLegacyEvent({
      type: "state_updated",
      path: "experience.added",
      value: visibleWorkExperiences.length,
    });
  };

  const handleGenerateSuggestions = async (index: number) => {
    const experience = workExpInfo[index];
    if (!experience?.jobCompanyName.trim() || !experience.jobtitleAndResp.trim()) {
      setWorkExpError(t`Add the company and role details before asking for bullet ideas.`);
      return;
    }

    setSuggestionLoadingIndex(index);
    setIsLoading(true);
    recordLegacyToolStarted(generateExperienceSuggestionsToolName);

    try {
      const suggestions = await aiService.generateExperienceSuggestions(
        jobDescription,
        experience.jobCompanyName,
        experience.jobtitleAndResp,
      );
      updateWorkExpInfoAiSugg(index, suggestions);
      recordLegacyStateUpdate(`experience.${index}.suggestedBullets`, suggestions);
      recordLegacyToolFinished(generateExperienceSuggestionsToolName);
    } catch (error) {
      console.error(error);
      setWorkExpError(
        t`I couldn't generate suggestions right now. You can still write your own impact.`,
      );
    } finally {
      setSuggestionLoadingIndex(null);
      setIsLoading(false);
    }
  };

  const handleUseSuggestion = (index: number, suggestion: string) => {
    updateWorkExpInfojobExp(index, suggestion);
    recordLegacyStateUpdate(`experience.${index}.selectedBullet`, suggestion);
  };

  const handleGenerateSummary = async () => {
    try {
      setIsLoading(true);
      recordLegacyToolStarted(generateUserSummaryToolName);
      const summary = await aiService.generateUserSummary(jobDescription, workExpInfo, []);
      updateUserSummary(summary);
      recordLegacyStateUpdate("summary", summary);
      recordLegacyToolFinished(generateUserSummaryToolName);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitWorkExperience = async () => {
    if (!workExpInfo.some(hasUsableWorkExperience)) {
      setWorkExpError(t`Add at least one role, project, internship, or volunteer experience.`);
      return;
    }

    const workExperienceCount = filledWorkExperienceCount;

    recordLegacyEvent({
      type: "prompt_submitted",
      promptId: workExperiencePromptId,
      value: workExpInfo.filter(hasWorkExperienceContent),
    });

    pushMessages({
      type: "user",
      content: t`Added ${workExperienceCount} experience entries.`,
    });
    pushMessages({
      type: "ai",
      content: t`Great. I'll use this experience to shape your summary and role bullets.`,
    });

    await handleGenerateSummary();

    setIsSubmitted(true);
    setStep("SKILLS");
  };

  const handleChatFallback = (input: string) => {
    const trimmedInput = input.trim();
    const targetIndex = visibleWorkExperiences.findIndex(
      (experience) => !experience.jobCompanyName.trim(),
    );
    const nextIndex = targetIndex >= 0 ? targetIndex : visibleWorkExperiences.length;

    if (nextIndex === visibleWorkExperiences.length) {
      updateWorkExpInfoAddNewJob();
    }

    updateWorkExpInfoCompany(nextIndex, trimmedInput);
    recordLegacyStateUpdate(`experience.${nextIndex}.company`, trimmedInput);

    pushMessages({
      type: "user",
      content: trimmedInput,
    });
    pushMessages({
      type: "ai",
      content: t`I added that as a company. Complete the role details below.`,
    });

    setChatTextData("");
  };

  useEffect(() => {
    if (step === "WORK_EXP") {
      const initialMessages: messageType = [
        {
          type: "ai",
          content: t`Add your most relevant work experience. Include projects, internships, or volunteer work if they help tell the story.`,
          showIllustration: "job",
        },
      ];
      setShowWorkExp(true);
      setIsSubmitted(false);
      setWorkExpError("");
      setMessages(initialMessages);
      recordLegacyMessages(initialMessages);
      recordLegacyEvent({ type: "prompt_shown", promptId: workExperiencePromptId });
      focusTextarea();
    }
  }, [step]);

  useEffect(() => {
    if (step === "WORK_EXP" && chatTextData) {
      handleChatFallback(chatTextData);
    }
  }, [chatTextData]);

  return (
    <>
      {showWorkExp &&
        messages.map((message, index) => (
          <div key={`work-${index}`}>
            <div
              className={cn(
                "mx-1 mb-3 max-w-[80%] rounded-lg px-4 py-2 text-base shadow-md",
                message.type === "user"
                  ? "ml-auto bg-blue-100 text-blue-800"
                  : "bg-gray-100 text-gray-800",
              )}
            >
              {message.type === "ai" ? (
                <div className="flex items-center gap-2">
                  <div className="grow">
                    <TypingText message={message.content} />
                  </div>

                  {message.showIllustration === "job" && (
                    <div className="size-32 shrink-0">
                      <img
                        alt=""
                        className="float-right h-full scale-125 object-cover object-center"
                        src="/backgrounds/workExpChat.svg"
                      />
                    </div>
                  )}
                </div>
              ) : (
                message.content
              )}
            </div>
          </div>
        ))}

      {showWorkExp && step === "WORK_EXP" && !isSubmitted && (
        <div className="mx-1 mb-5 w-[min(100%,52rem)] rounded-lg border border-border bg-background p-4 shadow-sm">
          <div className="mb-4 flex flex-col gap-1">
            <h3 className="text-sm font-semibold text-foreground">{t`Experience`}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t`Focus on roles and projects that make you credible for the target job.`}
            </p>
          </div>

          <div className="space-y-4">
            {visibleWorkExperiences.map((experience, index) => {
              const entryNumber = index + 1;
              const suggestionCount = experience.aiSugg.length;

              return (
                <section
                  key={`experience-${index}`}
                  aria-label={t`Experience entry ${entryNumber}`}
                  className="rounded-md border border-border bg-secondary/20 p-3"
                >
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <h4 className="text-sm font-semibold text-foreground">
                      {t`Experience ${entryNumber}`}
                    </h4>
                    {!hasWorkExperienceContent(experience) && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">{t`Required`}</span>
                    )}
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="min-w-0 space-y-1.5">
                      <Label
                        htmlFor={`experience-${index}-company`}
                        className="text-xs font-medium text-foreground"
                      >
                        {t`Company or project`}
                      </Label>
                      <Input
                        id={`experience-${index}-company`}
                        value={experience.jobCompanyName}
                        autoComplete="organization"
                        placeholder={t`Acme, Portfolio project, Community program`}
                        onChange={(event) =>
                          updateWorkExperienceField(index, "company", event.target.value)
                        }
                        className="!h-10 !rounded-md !border bg-background text-sm shadow-none focus-visible:ring-2 focus-visible:ring-primary/30"
                      />
                    </div>

                    <div className="min-w-0 space-y-1.5">
                      <Label
                        htmlFor={`experience-${index}-duration`}
                        className="text-xs font-medium text-foreground"
                      >
                        {t`Dates`}
                      </Label>
                      <Input
                        id={`experience-${index}-duration`}
                        value={experience.jobDuration}
                        placeholder={t`Jan 2021 - Present`}
                        onChange={(event) =>
                          updateWorkExperienceField(index, "duration", event.target.value)
                        }
                        className="!h-10 !rounded-md !border bg-background text-sm shadow-none focus-visible:ring-2 focus-visible:ring-primary/30"
                      />
                    </div>
                  </div>

                  <div className="mt-3 space-y-1.5">
                    <Label
                      htmlFor={`experience-${index}-title`}
                      className="text-xs font-medium text-foreground"
                    >
                      {t`Role and responsibilities`}
                    </Label>
                    <textarea
                      id={`experience-${index}-title`}
                      value={experience.jobtitleAndResp}
                      rows={3}
                      placeholder={t`Software Engineer responsible for building customer-facing React features`}
                      onChange={(event) =>
                        updateWorkExperienceField(index, "title", event.target.value)
                      }
                      className="min-h-24 w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm shadow-none transition-colors placeholder:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                    />
                  </div>

                  <div className="mt-3 space-y-1.5">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <Label
                        htmlFor={`experience-${index}-impact`}
                        className="text-xs font-medium text-foreground"
                      >
                        {t`Impact or achievement`}
                      </Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-fit gap-2"
                        disabled={suggestionLoadingIndex === index}
                        onClick={() => handleGenerateSuggestions(index)}
                      >
                        <Sparkles size={14} aria-hidden="true" />
                        {suggestionLoadingIndex === index ? t`Generating` : t`Suggest bullets`}
                      </Button>
                    </div>
                    <textarea
                      id={`experience-${index}-impact`}
                      value={experience.jobUniqueImpact}
                      rows={3}
                      placeholder={t`Improved onboarding speed by simplifying the application flow`}
                      onChange={(event) =>
                        updateWorkExperienceField(index, "impact", event.target.value)
                      }
                      className="min-h-24 w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm shadow-none transition-colors placeholder:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                    />
                  </div>

                  {suggestionCount > 0 && (
                    <div className="mt-3 rounded-md bg-background p-3">
                      <Label className="mb-2 block text-xs font-medium text-foreground">
                        {t`Suggested bullets`}
                      </Label>
                      <div className="space-y-2">
                        {experience.aiSugg.map((suggestion) => (
                          <button
                            key={suggestion}
                            type="button"
                            onClick={() => handleUseSuggestion(index, suggestion)}
                            className="w-full rounded-md border border-border px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-secondary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </section>
              );
            })}
          </div>

          {workExpError && <p className="mt-3 text-xs font-medium text-error">{workExpError}</p>}

          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <Button
              type="button"
              variant="outline"
              size="md"
              className="gap-2"
              onClick={handleAddWorkExperience}
            >
              <Plus size={16} aria-hidden="true" />
              {t`Add another`}
            </Button>

            <Button type="button" size="md" className="gap-2" onClick={handleSubmitWorkExperience}>
              <CheckCircle2 size={16} aria-hidden="true" />
              {t`Continue`}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};
